import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { ChevronLeft } from "lucide-react";
import ResultsTable from "./ResultsTable";
import ScoringPanel from "./ScoringPanel"; 
import { isChippingTest, isWedgingTest, isBunkerTest } from "../level3/scoring/utils";
import { getMaxAttempts } from "@/utils/level1/maxAttempts";

interface ScoresState {
  [key: string]: (number | null)[];
}

interface LevelInfo {
  level: string;
}

interface TestInterfaceProps {
  selectedTest: string;
  currentPoint: string;
  currentAttempt: number;
  scores: ScoresState;
  onBackToTests: () => void;
  onFinish: () => void;
  onScoreChange: (score: number, tendency?: any) => void;
  onCancelLastEntry: () => void;
  getTestTitle: (testId: string) => string;
  getTestDescription: (testId: string, path?: string) => string;
  getTestPoints: (testId: string) => string[];
  getTestDistances: (testId: string) => Record<string, string>;
  getTableHeadTitle: () => string;
  getFormattedPoint: (point: string) => string;
  getPointTotal: (point: string) => number;
  getGrandTotal: () => number;
  levels: Record<string, string | LevelInfo>;
}

const TestInterface: React.FC<TestInterfaceProps> = ({
  selectedTest,
  currentPoint,
  currentAttempt,
  scores,
  onBackToTests,
  onFinish,
  onScoreChange,
  onCancelLastEntry,
  getTestTitle,
  getTestDescription,
  getTestPoints,
  getTestDistances,
  getTableHeadTitle,
  getFormattedPoint,
  getPointTotal,
  getGrandTotal,
  levels
}) => {
  const { t } = useTranslation('training');
  const currentLevel = 1; // We're explicitly in level 1
  const maxAttempts = getMaxAttempts(selectedTest);
  const testPoints = getTestPoints(selectedTest);
  
  // Récupérer les distances pour le point actuel
  const distances = getTestDistances(selectedTest);
  const distance = distances[currentPoint] || "";
  
  console.log("TestInterface - selectedTest:", selectedTest);
  console.log("TestInterface - currentPoint:", currentPoint);
  console.log("TestInterface - testPoints:", testPoints);
  console.log("TestInterface - distances:", distances);
  console.log("TestInterface - distance for current point:", distance);
  console.log("TestInterface - scores:", scores);
  
  const isChipping = isChippingTest(selectedTest);

  useEffect(() => {
    // Log pour déboguer
    console.log("TestInterface - Distances mises à jour:", getTestDistances(selectedTest));
    console.log("TestInterface - Points:", testPoints);
  }, [selectedTest, testPoints, getTestDistances]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="mb-12"
    >
      <Card className="w-full mx-auto mt-2 md:mt-6">
        <CardHeader className="pb-2 md:pb-6">
          <div className="flex justify-between items-center">
            <Button 
              variant="outline" 
              className="mb-2"
              onClick={onBackToTests}
              type="button"
            >
              <ChevronLeft className="mr-1" /> {t('level1.interface.backToTests')}
            </Button>
          </div>
          <CardTitle className="text-xl md:text-2xl text-golf-700">{getTestTitle(selectedTest)}</CardTitle>
          <CardDescription className="text-base mt-2">
            {getTestDescription(selectedTest, window.location.pathname)}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 md:space-y-6">
            <ScoringPanel 
              testId={selectedTest}
              currentPoint={currentPoint}
              currentAttempt={currentAttempt}
              maxAttempts={maxAttempts}
              distance={distance}
              onScoreChange={onScoreChange}
              onCancelLastEntry={onCancelLastEntry}
              isChippingTest={isChipping}
              currentLevel={currentLevel}
            />

            <ResultsTable 
              selectedTest={selectedTest}
              scores={scores}
              testPoints={testPoints}
              getTestDistances={getTestDistances}
              getTableHeadTitle={getTableHeadTitle}
              getFormattedPoint={getFormattedPoint}
              getPointTotal={getPointTotal}
              getGrandTotal={getGrandTotal}
              levels={levels}
            />
            
            <div className="flex justify-end mt-4">
              <Button onClick={onFinish} className="bg-golf-600 hover:bg-golf-700">
                {t('level1.interface.finish')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default TestInterface;